import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, ErrorAlert, Field, InfoField } from "src/components";
import DropdownAnimation from "src/components/UI/dropdownAnimation/DropdownAnimation";
import { CxmWithdrawalState } from "src/hooks";
import { CxmPayoutAccount } from "src/api";
import { formatNumberToLocale } from "src/utils";
import CxmPayoutAccountForm from "./CxmPayoutAccountForm";
import "./cxm-form.scss";
import "./cxm-withdrawal-form.scss";

/** `Usdt` / `AccountNumber` are CXM's internal type names, not user-facing. */
const TYPE_LABELS: Record<string, string> = {
  Usdt: "USDT",
  AccountNumber: "Bank account",
};

/**
 * A crypto address is recognisable by its head and tail; an account number
 * only by its last digits. Falls back to the type alone when neither applies.
 */
function accountLabel(account: CxmPayoutAccount, knownKeys: string[]): string {
  const type = TYPE_LABELS[account.type] ?? account.type;
  const value = knownKeys
    .map((key) => account.details?.[key])
    .find((v) => String(v ?? "").trim());

  if (!value) return type;

  const text = String(value);
  if (text.startsWith("0x") && text.length > 12) {
    return `${type} — ${text.slice(0, 6)}…${text.slice(-4)}`;
  }
  return `${type} — ****${text.slice(-4)}`;
}

/**
 * Single-screen CXM payout form: method → payout account → amount → submit.
 * Styles are its own (`cxm-*`); nothing here borrows from the deposit or
 * Alfred sheets, so a change to either cannot restyle this flow.
 *
 * The confirmation card deliberately makes no promise about timing: the same
 * amount settled instantly one day and went to manual review the next, because
 * review tracks the partner's accumulated volume rather than the amount, and
 * nothing in the API exposes it.
 */
const CxmWithdrawalForm: React.FC<{ withdrawal: CxmWithdrawalState }> = ({
  withdrawal: w,
}): JSX.Element => {
  const { t } = useTranslation();
  const { currency_id, precision } = w.currency;
  const displayPrecision = Math.min(precision || 2, 2);

  const methodOptions = useMemo(
    () => w.methods.map((m) => ({ key: m.methodId, value: m.displayName })),
    [w.methods]
  );

  // Keys the live schema actually declares. Anything else in `details` is
  // junk the gateway accepted — it validates nothing — so an account with no
  // recognisable field is hidden rather than shown as "****nope".
  const schemaKeys = useMemo(
    () => w.requirementFields.map((f) => f.name),
    [w.requirementFields]
  );

  const usableAccounts = useMemo(
    () =>
      schemaKeys.length
        ? w.accounts.filter((a) =>
            schemaKeys.some((key) => String(a.details?.[key] ?? "").trim())
          )
        : w.accounts,
    [w.accounts, schemaKeys]
  );

  const accountOptions = useMemo(
    () =>
      usableAccounts.map((a) => ({
        key: String(a.id),
        value: accountLabel(a, schemaKeys),
      })),
    [usableAccounts, schemaKeys]
  );

  const limitsHint = useMemo(() => {
    const { min, max } = w.limits;
    if (min === null && max === null) return undefined;
    if (max === null) return t("CXM.WITHDRAWAL.MIN_ONLY", { min, currency: currency_id });
    if (min === null) return t("CXM.WITHDRAWAL.MAX_ONLY", { max, currency: currency_id });
    return t("CXM.WITHDRAWAL.LIMITS", { min, max, currency: currency_id });
  }, [w.limits, currency_id, t]);

  if (w.submitted) {
    return (
      <section className="cxm-form cxm-form--terminal">
        <div className="cxm-success">
          <svg className="cxm-success__mark" viewBox="0 0 56 56" aria-hidden="true">
            <circle className="cxm-success__ring" cx="28" cy="28" r="26" />
            <path className="cxm-success__check" d="M17 29.5 L24.5 37 L39 22" />
          </svg>
        </div>
        <h2 className="cxm-form__title">{t("CXM.WITHDRAWAL.SUBMITTED_TITLE")}</h2>
        <p className="cxm-form__text">{t("CXM.WITHDRAWAL.SUBMITTED_TEXT")}</p>
        <InfoField
          label={t("CXM.WITHDRAWAL.YOU_RECEIVE")}
          value={`${w.submitted.amount} ${currency_id}`}
        />
        <Button title={t("CXM.WITHDRAWAL.DONE")} className="cxm-button" onClick={w.reset} />
      </section>
    );
  }

  // An empty catalogue means the operator granted no payment rules for this
  // partner/user pair — a normal state, not a failure to retry.
  if (!w.methodsLoading && w.methods.length === 0) {
    return (
      <section className="cxm-form cxm-form--terminal">
        <svg className="cxm-form__blocked-mark" viewBox="0 0 44 44" aria-hidden="true">
          <circle cx="22" cy="22" r="20" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.35" />
          <path d="M22 13v13" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
          <circle cx="22" cy="31.5" r="1.6" fill="currentColor" />
        </svg>
        <h2 className="cxm-form__title">{t("CXM.WITHDRAWAL.UNAVAILABLE_TITLE")}</h2>
        <p className="cxm-form__text">{t("CXM.WITHDRAWAL.UNAVAILABLE_TEXT")}</p>
      </section>
    );
  }

  if (w.addAccountOpen) {
    return (
      <div className="cxm-payout-overlay" role="dialog" aria-modal="true">
        <CxmPayoutAccountForm
          fields={w.requirementFields}
          submitting={w.addAccountLoading}
          error={w.addAccountError}
          onSubmit={w.handleAddAccount}
          onCancel={() => w.setAddAccountOpen(false)}
        />
      </div>
    );
  }

  const noAccounts = accountOptions.length === 0;
  const amountDisabled = w.submitLoading || !w.selectedMethod;
  const amountError = w.amountTouched ? w.amountError : undefined;

  return (
    <form className="cxm-form" onSubmit={w.handleSubmit}>
      {methodOptions.length > 1 && (
        <div className="cxm-method-row">
          <DropdownAnimation
            label={t("CXM.WITHDRAWAL.METHOD")}
            selectedValue={w.selectedMethodId ?? ""}
            options={methodOptions}
            placeholder={t("CXM.WITHDRAWAL.SELECT_METHOD")}
            onChange={(field) => w.setSelectedMethodId(field.key)}
            disabled={w.methodsLoading || w.submitLoading}
          />
        </div>
      )}

      {/* With nothing to withdraw to, adding an account IS the task, so it
          gets the full-width control. It used to be a 44px `+` beside a
          disabled picker whose placeholder carried the instruction. */}
      {noAccounts && !w.accountsLoading ? (
        <>
          <Button
            title={t("CXM.WITHDRAWAL.ADD_ACCOUNT")}
            className="cxm-button"
            type="button"
            secondary
            onClick={() => w.setAddAccountOpen(true)}
            disabled={
              w.submitLoading ||
              w.requirementsLoading ||
              w.requirementFields.length === 0
            }
            pending={w.requirementsLoading}
          />
          {/* Without this the only route to a payout account disables itself
              in silence whenever the schema request fails, which reads as a
              removed feature rather than a broken call. */}
          {!w.requirementsLoading && w.requirementFields.length === 0 && (
            <ErrorAlert
              message={
                w.requirementsError ?? t("CXM.WITHDRAWAL.NO_ACCOUNT_FORM")
              }
            />
          )}
        </>
      ) : (
        <div className="cxm-account-row">
          <DropdownAnimation
            wrapperClass="cxm-account-row__dropdown"
            label={t("CXM.WITHDRAWAL.PAYOUT_ACCOUNT")}
            selectedValue={w.selectedAccountId ? String(w.selectedAccountId) : ""}
            options={accountOptions}
            placeholder={
              w.accountsLoading
                ? t("CXM.WITHDRAWAL.LOADING_ACCOUNTS")
                : t("CXM.WITHDRAWAL.SELECT_ACCOUNT")
            }
            onChange={(field) => w.setSelectedAccountId(Number(field.key))}
            disabled={w.accountsLoading || w.submitLoading}
          />

          {w.requirementFields.length > 0 && (
            <button
              type="button"
              className="cxm-account-row__add"
              onClick={() => w.setAddAccountOpen(true)}
              disabled={w.submitLoading}
              aria-label={t("CXM.PAYOUT_ACCOUNT.TITLE")}
              title={t("CXM.PAYOUT_ACCOUNT.TITLE")}
            >
              +
            </button>
          )}
        </div>
      )}

      <div className="cxm-amount">
        <Field
          label={
            <span className="field-label-content">
              {t("CXM.WITHDRAWAL.AMOUNT", { currency: currency_id })}
              <button
                type="button"
                className="cxm-amount__max"
                onClick={w.handleMaxAmount}
                disabled={amountDisabled || w.maxSendable <= 0}
              >
                {t("CXM.WITHDRAWAL.MAX")}
              </button>
            </span>
          }
          value={w.amount}
          onChange={w.handleAmountChange}
          inputMode="decimal"
          disabled={amountDisabled}
          error={amountError}
          /* The limits are a constraint the user needs *while* typing, so they
             live on their own line rather than in the placeholder, where the
             first keystroke wiped them. The error takes the same line, because
             the shared field otherwise shows it only in a hover tooltip — which
             a touch user never opens. */
          assist={amountError ?? limitsHint}
        />
      </div>

      <InfoField
        className="info-field--muted cxm-figure"
        label={t("CXM.WITHDRAWAL.AVAILABLE")}
        value={`${formatNumberToLocale(w.balance, displayPrecision)} ${currency_id}`}
      />

      {/* Fee is structurally zero at CXM — stated rather than computed. */}
      <InfoField
        className="info-field--muted cxm-figure"
        label={t("CXM.WITHDRAWAL.GATEWAY_FEE")}
        value={`0 ${currency_id}`}
      />

      <ErrorAlert message={w.submitError ?? w.methodsError} />

      <Button
        title={t("CXM.WITHDRAWAL.WITHDRAW")}
        className="cxm-button"
        type="submit"
        disabled={w.submitDisabled}
        pending={w.submitLoading}
      />
    </form>
  );
};

export default CxmWithdrawalForm;
